import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import AdminCrudModal from '../../components/AdminCrudModal';
import axiosInstance from '../../api/axios';
import './Pendaftaran.css';

const STATUS_LABELS = {
  pending:    { label: 'Pending',      color: '#B45309', bg: '#FEF3C7', border: '#FDE68A' },
  confirmed:  { label: 'Dikonfirmasi', color: '#1D4ED8', bg: '#DBEAFE', border: '#BFDBFE' },
  checked_in: { label: 'Check-in',     color: '#6D28D9', bg: '#EDE9FE', border: '#DDD6FE' },
  completed:  { label: 'Selesai',      color: '#047857', bg: '#D1FAE5', border: '#A7F3D0' },
  cancelled:  { label: 'Dibatalkan',   color: '#B91C1C', bg: '#FEE2E2', border: '#FECACA' },
};

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] ?? { label: status, color: '#374151', bg: '#F3F4F6', border: '#E5E7EB' };
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      color: s.color,
      backgroundColor: s.bg,
      border: `1px solid ${s.border}`
    }}>
      {s.label}
    </span>
  );
}

const fmtTanggal = (val) => {
  if (!val) return '-';
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('id-ID', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  }
  return s;
};

export default function Pendaftaran() {
  const [list, setList]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate]   = useState('');
  const [notice, setNotice]           = useState('');
  const [error, setError]             = useState('');

  // State Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage]       = useState(1);
  const [totalItems, setTotalItems]   = useState(0);
  const [perPage, setPerPage]         = useState(15);

  const [detail, setDetail]               = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [verifyTarget, setVerifyTarget]   = useState(null);
  const [verifyStatus, setVerifyStatus]   = useState('confirmed');
  const [verifyLoading, setVerifyLoading] = useState(false);

  /* ── Fetch Data Utama ── */
  const fetchList = useCallback(async (status = '', date = '', page = 1) => {
    setLoading(true);
    setError('');
    try {
      const params = { page };
      if (status) params.status = status;
      if (date)   params.tanggal_pendaftaran = date;

      const res = await axiosInstance.get('/admin/pendaftaran', { params });
      const payload = res.data;

      if (payload && typeof payload === 'object' && 'current_page' in payload) {
        setList(payload.data || []);
        setCurrentPage(payload.current_page || 1);
        setLastPage(payload.last_page || 1);
        setTotalItems(payload.total || 0);
        setPerPage(payload.per_page || 15);
      } 
      else if (payload?.data && typeof payload.data === 'object' && 'current_page' in payload.data) {
        setList(payload.data.data || []);
        setCurrentPage(payload.data.current_page || 1);
        setLastPage(payload.data.last_page || 1);
        setTotalItems(payload.data.total || 0);
        setPerPage(payload.data.per_page || 15);
      }
      else {
        const rawArray = Array.isArray(payload) ? payload : (payload?.data || []);
        setList(rawArray);
        setCurrentPage(1);
        setLastPage(1);
        setTotalItems(rawArray.length);
      }

    } catch (err) {
      setError(err.response?.data?.message ?? 'Gagal memuat data pendaftaran.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList(filterStatus, filterDate, currentPage);
  }, [fetchList, filterStatus, filterDate, currentPage]);

  const handleFilterStatus = (e) => {
    setFilterStatus(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterDate = (e) => {
    setFilterDate(e.target.value);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setFilterStatus('');
    setFilterDate('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= lastPage) {
      setCurrentPage(newPage);
    }
  };

  const getRowNumber = (index) => {
    return (currentPage - 1) * perPage + index + 1;
  };

  /* ── Detail ── */
  const handleDetail = async (id) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await axiosInstance.get(`/admin/pendaftaran/${id}`);
      setDetail(res.data?.data ?? res.data);
    } catch (err) {
      setError(err.response?.data?.message ?? 'Gagal memuat detail.');
    } finally {
      setDetailLoading(false);
    }
  };

  /* ── Verifikasi ── */
  const handleVerify = async () => {
    if (!verifyTarget) return;
    setVerifyLoading(true);
    try {
      await axiosInstance.post(`/admin/pendaftaran/${verifyTarget.id}/verifikasi`, {
        status: verifyStatus,
      });
      setVerifyTarget(null);
      setNotice(`Pendaftaran berhasil di-${verifyStatus === 'confirmed' ? 'konfirmasi' : 'tolak'}.`);
      fetchList(filterStatus, filterDate, currentPage);
    } catch (err) {
      setError(err.response?.data?.message ?? 'Gagal memverifikasi pendaftaran.');
    } finally {
      setVerifyLoading(false);
    }
  };

  /* ── Update Status ── */
  const handleUpdateStatus = async (id, status) => {
    try {
      await axiosInstance.put(`/admin/pendaftaran/${id}`, { status });
      setNotice('Status berhasil diperbarui.');
      fetchList(filterStatus, filterDate, currentPage);
      if (detail?.id === id) handleDetail(id);
    } catch (err) {
      setError(err.response?.data?.message ?? 'Gagal memperbarui status.');
    }
  };

  /* ── Batalkan ── */
  const handleCancel = async (id) => {
    if (!window.confirm('Batalkan pendaftaran ini?')) return;
    try {
      await axiosInstance.delete(`/admin/pendaftaran/${id}`);
      setNotice('Pendaftaran dibatalkan.');
      fetchList(filterStatus, filterDate, currentPage);
    } catch (err) {
      setError(err.response?.data?.message ?? 'Gagal membatalkan pendaftaran.');
    }
  };

  const hasActiveFilter = filterStatus || filterDate;

  return (
    <AdminLayout title="">
      
      {/* ── HEADER HALAMAN ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#111827', margin: 0 }}>Kelola Pendaftaran</h1>
          <p style={{ color: '#6B7280', margin: '4px 0 0', fontSize: '14px' }}>Verifikasi dan pantau riwayat pendaftaran kunjungan pasien</p>
        </div>
      </div>

      {/* ── ALERTS / NOTIFIKASI ── */}
      {notice && (
        <div style={{ padding: '12px 16px', backgroundColor: '#D1FAE5', color: '#065F46', borderRadius: '10px', fontSize: '14px', marginBottom: '16px', border: '1px solid #A7F3D0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{notice}</span>
          <button onClick={() => setNotice('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065F46', fontWeight: 'bold' }}>✕</button>
        </div>
      )}
      {error && (
        <div style={{ padding: '12px 16px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '10px', fontSize: '14px', marginBottom: '16px', border: '1px solid #FECACA', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991B1B', fontWeight: 'bold' }}>✕</button>
        </div>
      )}

      {/* ── FILTER & TOOLBAR CARD ── */}
      <div style={{ background: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '16px 20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#4B5563' }}>Status</label>
              <select
                value={filterStatus}
                onChange={handleFilterStatus}
                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '13px', outline: 'none', backgroundColor: '#F9FAFB' }}
              >
                <option value="">Semua Status</option>
                {Object.entries(STATUS_LABELS).map(([val, { label }]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#4B5563' }}>Tanggal</label>
              <input
                type="date"
                value={filterDate}
                onChange={handleFilterDate}
                style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '13px', outline: 'none', backgroundColor: '#F9FAFB' }}
              />
            </div>

            {hasActiveFilter && (
              <button
                onClick={handleReset}
                style={{ marginTop: '18px', padding: '8px 14px', backgroundColor: '#F3F4F6', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
              >
                Reset Filter
              </button>
            )}
          </div>

          <div>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', backgroundColor: '#F3F4F6', padding: '6px 12px', borderRadius: '20px' }}>
              Total: {loading ? '...' : `${totalItems} Data`}
            </span>
          </div>

        </div>
      </div>

      {/* ── PANEL TABEL ── */}
      <div style={{ background: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: '#6B7280', fontSize: '14px' }}>⏳ Memuat data pendaftaran...</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={thStyle}>No</th>
                    <th style={thStyle}>No. Antrian</th>
                    <th style={thStyle}>Pasien</th>
                    <th style={thStyle}>Dokter</th>
                    <th style={thStyle}>Tanggal</th>
                    <th style={thStyle}>Jam</th>
                    <th style={thStyle}>Keluhan</th>
                    <th style={thStyle}>Status</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: 'center', padding: '48px 20px', color: '#9CA3AF', fontSize: '14px' }}>
                        {hasActiveFilter ? 'Tidak ada data untuk filter yang dipilih' : 'Belum ada data pendaftaran'}
                      </td>
                    </tr>
                  ) : (
                    list.map((item, i) => (
                      <tr key={item.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={tdStyle}>{getRowNumber(i)}</td>
                        <td style={{ ...tdStyle, fontWeight: '700', color: '#2563EB' }}>{item.no_antrian ?? '-'}</td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: '600', color: '#111827' }}>{item.pasien?.nama ?? item.pasien_id}</div>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ color: '#374151' }}>{item.dokter?.nama ?? item.dokter_id}</div>
                        </td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtTanggal(item.tanggal_pendaftaran)}</td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#4B5563' }}>{item.estimasi_jam_kehadiran || item.jam_kunjungan || '-'}</td>
                        <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.keluhan ?? '-'}
                        </td>
                        <td style={tdStyle}><StatusBadge status={item.status} /></td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <button onClick={() => handleDetail(item.id)} style={btnActionStyle('#2563EB')}>
                              Detail
                            </button>

                            {item.status === 'pending' && (
                              <button onClick={() => { setVerifyTarget(item); setVerifyStatus('confirmed'); }} style={btnActionStyle('#059669')}>
                                Verifikasi
                              </button>
                            )}
                            {item.status === 'confirmed' && (
                              <button onClick={() => handleUpdateStatus(item.id, 'checked_in')} style={btnActionStyle('#7C3AED')}>
                                Check-in
                              </button>
                            )}
                            {item.status === 'checked_in' && (
                              <button onClick={() => handleUpdateStatus(item.id, 'completed')} style={btnActionStyle('#047857')}>
                                Selesai
                              </button>
                            )}
                            {!['completed', 'cancelled'].includes(item.status) && (
                              <button onClick={() => handleCancel(item.id)} style={btnActionStyle('#EF4444')}>
                                Batal
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* ── PAGINASI ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: '1px solid #E5E7EB', flexWrap: 'wrap', gap: '12px', background: '#F9FAFB' }}>
              <div style={{ fontSize: '13px', color: '#6B7280' }}>
                Halaman <strong style={{ color: '#111827' }}>{currentPage}</strong> dari <strong style={{ color: '#111827' }}>{lastPage}</strong> (Total <strong style={{ color: '#111827' }}>{totalItems}</strong> Data)
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{ ...btnPagingStyle, opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                >
                  &laquo; Prev
                </button>

                {Array.from({ length: lastPage }, (_, idx) => idx + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    style={{
                      ...btnPagingStyle,
                      backgroundColor: currentPage === p ? '#2563EB' : '#FFFFFF',
                      color: currentPage === p ? '#FFFFFF' : '#374151',
                      borderColor: currentPage === p ? '#2563EB' : '#D1D5DB',
                      fontWeight: currentPage === p ? '700' : '500',
                    }}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === lastPage || lastPage === 0}
                  style={{ ...btnPagingStyle, opacity: (currentPage === lastPage || lastPage === 0) ? 0.5 : 1, cursor: (currentPage === lastPage || lastPage === 0) ? 'not-allowed' : 'pointer' }}
                >
                  Next &raquo;
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── MODAL DETAIL ── */}
      <AdminCrudModal
        open={detailLoading || Boolean(detail)}
        title="Detail Pendaftaran"
        onClose={() => setDetail(null)}
      >
        {detailLoading ? (
          <div style={{ padding: '30px', textAlign: 'center', color: '#6B7280' }}>Memuat detail...</div>
        ) : detail ? (
          <div style={{ padding: '4px' }}>
            <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden', marginBottom: '16px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <tbody>
                  {[
                    ['No. Antrian',   detail.no_antrian ?? '-'],
                    ['Status',        <StatusBadge status={detail.status} />],
                    ['Pasien',        detail.pasien?.nama ?? detail.pasien_id],
                    ['Dokter',        detail.dokter?.nama ?? detail.dokter_id],
                    ['Tanggal',       fmtTanggal(detail.tanggal_pendaftaran)],
                    ['Jam Kunjungan', detail.estimasi_jam_kehadiran || detail.jam_kunjungan || '-'],
                    ['Keluhan',       detail.keluhan ?? '-'],
                  ].map(([label, val], idx) => (
                    <tr key={label} style={{ backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#F9FAFB' }}>
                      <td style={{ padding: '10px 14px', fontWeight: '600', color: '#4B5563', width: '30%', borderBottom: '1px solid #F3F4F6', borderRight: '1px solid #E5E7EB' }}>
                        {label}
                      </td>
                      <td style={{ padding: '10px 14px', color: '#111827', borderBottom: '1px solid #F3F4F6' }}>
                        {val}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {detail.rekam_medis && (
              <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 8px', color: '#92400E', fontSize: '14px', fontWeight: '700' }}>Rekam Medis Terkait</h4>
                <div style={{ fontSize: '13px', color: '#78350F' }}>
                  <p style={{ margin: '0 0 4px' }}><strong>Diagnosis:</strong> {detail.rekam_medis.diagnosis ?? '-'}</p>
                  <p style={{ margin: 0 }}><strong>Catatan:</strong> {detail.rekam_medis.catatan ?? '-'}</p>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setDetail(null)} style={btnSecondaryStyle}>Tutup</button>
            </div>
          </div>
        ) : null}
      </AdminCrudModal>

      {/* ── MODAL VERIFIKASI ── */}
      <AdminCrudModal
        open={Boolean(verifyTarget)}
        title="Verifikasi Pendaftaran"
        onClose={() => setVerifyTarget(null)}
        size="sm"
      >
        {verifyTarget && (
          <div style={{ padding: '4px' }}>
            <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px' }}>
              <div style={{ marginBottom: '6px' }}><span style={{ color: '#6B7280' }}>Pasien:</span> <strong>{verifyTarget.pasien?.nama ?? verifyTarget.pasien_id}</strong></div>
              <div><span style={{ color: '#6B7280' }}>No. Antrian:</span> <strong style={{ color: '#2563EB' }}>{verifyTarget.no_antrian}</strong></div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                Keputusan <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <select
                value={verifyStatus}
                onChange={(e) => setVerifyStatus(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', outline: 'none' }}
              >
                <option value="confirmed">Konfirmasi (Terima)</option>
                <option value="rejected">Tolak Pendaftaran</option>
              </select>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setVerifyTarget(null)} disabled={verifyLoading} style={btnSecondaryStyle}>
                Batal
              </button>
              <button onClick={handleVerify} disabled={verifyLoading} style={btnPrimaryStyle}>
                {verifyLoading ? 'Memproses...' : 'Simpan Decisions'}
              </button>
            </div>
          </div>
        )}
      </AdminCrudModal>

    </AdminLayout>
  );
}

// ===================== STYLES =====================
const thStyle = {
  padding: '14px 16px',
  fontWeight: '700',
  fontSize: '12px',
  color: '#4B5563',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const tdStyle = {
  padding: '14px 16px',
  verticalAlign: 'middle',
  fontSize: '13px',
  color: '#374151'
};

const btnActionStyle = (bgColor) => ({
  padding: '6px 12px',
  backgroundColor: bgColor,
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: '600',
  transition: 'opacity 0.2s'
});

const btnPagingStyle = {
  padding: '6px 12px',
  border: '1px solid #D1D5DB',
  borderRadius: '6px',
  backgroundColor: '#FFFFFF',
  fontSize: '13px',
  color: '#374151',
  cursor: 'pointer'
};

const btnPrimaryStyle = {
  padding: '10px 18px',
  backgroundColor: '#2563EB',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600'
};

const btnSecondaryStyle = {
  padding: '10px 18px',
  backgroundColor: '#FFFFFF',
  color: '#374151',
  border: '1px solid #D1D5DB',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600'
};